import { useState, useCallback, useEffect, useRef } from 'react';
import {
  LiveKitRoom,
  useRoomContext,
  useConnectionState,
  RoomAudioRenderer,
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import '@livekit/components-styles';
import ConnectionForm from './components/ConnectionForm';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AvatarPanel from './components/AvatarPanel';
import WidgetPanel from './components/WidgetPanel';
import IntroScreen from './components/IntroScreen';
import AvatarPickerScreen from './components/AvatarPickerScreen';
import SectionPicker from './components/SectionPicker';
import { publishToAgent } from './lib/dataChannel';
import './App.css';

// Map picker section id → readable label for the navbar
const SECTION_LABELS = {
  avatar_picker: 'Avatar picker',
  screening: 'Screening',
  conceptual_qna: 'Conceptual Q&A',
  coding: 'Coding exercise',
  whiteboarding: 'White-boarding',
  proctoring: 'Proctoring',
};

function RoomView() {
  const room = useRoomContext();

  // ── Phase machine: avatar_picker → intro → interview ──
  const [phase, setPhase] = useState('avatar_picker');
  const [activeSection, setActiveSection] = useState(null);

  // ── Widget state (used in interview phase) ──
  const [activeWidget, setActiveWidget] = useState(null);
  const [codepadConfig, setCodepadConfig] = useState({ language: 'python', initialCode: '' });
  const [codeHighlights, setCodeHighlights] = useState([]);
  const [insertCode, setInsertCode] = useState(null);
  const [testCases, setTestCases] = useState(null);
  const [canvasDrawing, setCanvasDrawing] = useState(null);
  const [question, setQuestion] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [avatarList, setAvatarList] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [isSwappingAvatar, setIsSwappingAvatar] = useState(false);
  const isSwappingAvatarRef = useRef(false);
  const segMapRef = useRef({});
  const transcriptRef = useRef(null);
  const faceModelRef = useRef(null);
  const [faceModelReady, setFaceModelReady] = useState(false);

  // Preload BlazeFace model on mount so it's ready when proctor widget opens
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tf = await import('@tensorflow/tfjs');
        await tf.ready();
        const blazeface = await import('@tensorflow-models/blazeface');
        const model = await blazeface.load();
        if (!cancelled) {
          faceModelRef.current = model;
          setFaceModelReady(true);
        }
      } catch (err) {
        console.error('Failed to preload face detection model:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const connectionState = useConnectionState();

  // Enable local microphone and camera once fully connected
  useEffect(() => {
    if (!room || connectionState !== ConnectionState.Connected) return;
    room.localParticipant.setMicrophoneEnabled(true).catch((err) => {
      console.error('Failed to enable microphone:', err);
    });
    room.localParticipant.setCameraEnabled(true).catch((err) => {
      console.error('Failed to enable camera:', err);
    });
  }, [room, connectionState]);

  // ── Data channel: agent → client ──
  const onDataReceived = useCallback((payload) => {
    try {
      const text = new TextDecoder().decode(payload);
      const data = JSON.parse(text);

      switch (data.type) {
        // Phase transitions driven by agent
        case 'show_intro':
          setPhase('intro');
          break;

        // Widget commands (interview phase)
        case 'open_codepad':
          setCodepadConfig({
            language: data.language || 'python',
            initialCode: data.initial_code || '',
          });
          setCodeHighlights([]);
          setActiveWidget('codepad');
          setPhase('interview');
          break;

        case 'open_canvas': {
          let drawing = null;
          try {
            if (data.initial_drawing) {
              drawing = typeof data.initial_drawing === 'string'
                ? JSON.parse(data.initial_drawing) : data.initial_drawing;
            }
          } catch (e) { console.error('Failed to parse drawing:', e); }
          setCanvasDrawing(drawing);
          setActiveWidget('canvas');
          setPhase('interview');
          break;
        }

        case 'show_technical_question':
          setQuestion(data.question || '');
          setActiveWidget('question');
          setPhase('interview');
          break;

        case 'highlight_code':
          setCodeHighlights((prev) => [
            ...prev,
            {
              startLine: data.start_line || 1,
              endLine: data.end_line || 1,
              color: data.color || 'warning',
            },
          ]);
          break;

        case 'insert_code':
          setInsertCode({ code: data.code || '', language: data.language || 'python' });
          break;

        case 'add_test_cases':
          try {
            const parsed = typeof data.test_cases === 'string'
              ? JSON.parse(data.test_cases) : data.test_cases;
            setTestCases(parsed);
          } catch (e) {
            console.error('Failed to parse test cases:', e);
          }
          break;

        case 'open_proctor':
          setActiveWidget('proctor');
          setPhase('interview');
          break;

        case 'open_avatar_picker':
          setAvatarList(data.avatars || []);
          // Only land on avatar_picker if we haven't already moved past it.
          // A delayed open_avatar_picker (from _reset_to_default_avatar finishing
          // after the user already picked an avatar) must not override the
          // interview phase.
          setPhase((prev) => (prev === 'intro' || prev === 'interview') ? prev : 'avatar_picker');
          setActiveWidget(null);
          break;

        case 'avatar_ready':
          // Guard against stale avatar_ready arriving after a reset. If
          // isSwappingAvatarRef is false the user already bailed out (reset
          // or navigated away) and we must not auto-advance to intro.
          if (isSwappingAvatarRef.current) {
            isSwappingAvatarRef.current = false;
            setIsSwappingAvatar(false);
            setPhase((prev) => prev === 'avatar_picker' ? 'intro' : prev);
          }
          break;

        default:
          console.log('Received data:', data);
      }
    } catch (err) {
      console.error('Failed to parse agent data:', err);
    }
  }, []);

  useEffect(() => {
    if (!room) return;
    room.on('dataReceived', onDataReceived);
    return () => room.off('dataReceived', onDataReceived);
  }, [room, onDataReceived]);

  // ── Transcription ──
  useEffect(() => {
    if (!room) return;
    const handleTranscription = (segments, participant) => {
      const isAgent = participant?.kind === 4 ||
        participant?.identity?.includes('agent') ||
        participant?.identity?.includes('avatar');
      if (!isAgent) return;

      for (const seg of segments) {
        const segId = seg.id || seg.sid || `seg-${Date.now()}`;
        setTranscript((prev) => {
          const segMap = segMapRef.current;
          if (segMap[segId] !== undefined) {
            const updated = [...prev];
            updated[segMap[segId]] = { role: 'agent', text: seg.text, final: seg.final };
            return updated;
          }
          const idx = prev.length;
          segMap[segId] = idx;
          return [...prev, { role: 'agent', text: seg.text, final: seg.final }];
        });
      }
    };
    room.on('transcriptionReceived', handleTranscription);
    return () => room.off('transcriptionReceived', handleTranscription);
  }, [room]);

  useEffect(() => {
    if (transcriptRef.current) {
      const el = transcriptRef.current.querySelector('.transcript-list');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [transcript]);

  const resetSessionState = useCallback(() => {
    segMapRef.current = {};
    isSwappingAvatarRef.current = false;
    setTranscript([]);
    setActiveSection(null);
    setActiveWidget(null);
    setCodepadConfig({ language: 'python', initialCode: '' });
    setCodeHighlights([]);
    setInsertCode(null);
    setTestCases(null);
    setCanvasDrawing(null);
    setQuestion('');
    setSelectedAvatarId(null);
    setIsSwappingAvatar(false);
    setPhase('avatar_picker');
  }, []);

  // ── Section picker: click → publish event + immediately clear old widget ──
  const selectSection = useCallback((sectionId) => {
    if (sectionId === 'avatar_picker') {
      resetSessionState();
      publishToAgent(room, 'CLIENT_RESET_SESSION', {});
      return;
    }
    setActiveSection(sectionId);
    setActiveWidget(null);   // clear widget immediately so UI responds at once
    setPhase('interview');
    publishToAgent(room, 'CLIENT_SELECT_SECTION', {
      section_type: sectionId,
      source: 'picker',
    });
  }, [room, resetSessionState]);

  const closeWidget = useCallback(() => setActiveWidget(null), []);

  const handleSelectAvatar = useCallback((avatar) => {
    setSelectedAvatarId(avatar.id);
    publishToAgent(room, 'CLIENT_SELECT_AVATAR', {
      avatar_id: avatar.id,
      avatar_name: avatar.name,
    });
    segMapRef.current = {};
    isSwappingAvatarRef.current = true;
    setIsSwappingAvatar(true);
    setTranscript([]);
    setActiveSection(null);
    setActiveWidget(null);
  }, [room]);

  // Safety: dismiss the swap overlay even if `avatar_ready` never arrives.
  useEffect(() => {
    if (!isSwappingAvatar) return;
    const t = setTimeout(() => {
      isSwappingAvatarRef.current = false;
      setIsSwappingAvatar(false);
      setPhase((prev) => prev === 'avatar_picker' ? 'intro' : prev);
    }, 15000);
    return () => clearTimeout(t);
  }, [isSwappingAvatar]);

  const hasWidget = activeWidget !== null;
  const isProctor = activeWidget === 'proctor';

  const sectionNavLabel = activeSection ? SECTION_LABELS[activeSection] : undefined;

  return (
    <div className="room-layout">
      <Navbar sectionLabel={sectionNavLabel} />

      {/* ── Phase content ── */}
      <div className="room-body">
        {phase === 'avatar_picker' && !isSwappingAvatar && (
          <AvatarPickerScreen
            avatars={avatarList}
            selectedId={selectedAvatarId}
            onSelect={handleSelectAvatar}
          />
        )}

        {phase === 'avatar_picker' && isSwappingAvatar && (
          <div className="avatar-swap-overlay">
            <div className="avatar-swap-spinner" />
            <p className="avatar-swap-label">Starting your session…</p>
            <p className="avatar-swap-sub">Hang on a few seconds.</p>
          </div>
        )}

        {phase === 'intro' && <IntroScreen transcript={transcript} />}

        {phase === 'interview' && (
          <>
            {/* AvatarPanel always mounted — never use display:none on it
                since that pauses the <video> and re-attach causes a flash. */}
            <div
              className={`main-stage ${hasWidget ? 'with-widget' : ''} ${isProctor ? 'proctor-mode' : ''}`}
            >
              <AvatarPanel transcript={transcript} hideUserPip={isProctor} />
            </div>

            {hasWidget && (
              <div className={`right-panel ${isProctor ? 'right-panel-narrow' : ''}`}>
                <WidgetPanel
                  activeWidget={activeWidget}
                  codepadConfig={codepadConfig}
                  codeHighlights={codeHighlights}
                  insertCode={insertCode}
                  testCases={testCases}
                  canvasDrawing={canvasDrawing}
                  question={question}
                  onCloseWidget={closeWidget}
                  faceModel={faceModelRef}
                  faceModelReady={faceModelReady}
                />
              </div>
            )}

            {/* Avatar swap loading overlay during mid-session avatar switch */}
            {isSwappingAvatar && (
              <div className="avatar-swap-overlay">
                <div className="avatar-swap-spinner" />
                <p className="avatar-swap-label">Switching avatar…</p>
                <p className="avatar-swap-sub">Hang on a few seconds.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Section picker is hidden while on the avatar picker screen */}
      {phase !== 'avatar_picker' && (
        <SectionPicker activeSection={activeSection} onSelect={selectSection} />
      )}

      <Footer />
      <RoomAudioRenderer />
    </div>
  );
}

export default function App() {
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Auto-join from URL params: ?url=wss://...&token=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('url');
    const token = params.get('token');
    if (url && token) {
      setIsConnecting(true);
      setConnectionDetails({ url, token });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = useCallback((url, token) => {
    setIsConnecting(true);
    setConnectionDetails({ url, token });
  }, []);

  const handleConnected = useCallback(() => setIsConnecting(false), []);

  const handleDisconnected = useCallback(() => {
    setConnectionDetails(null);
    setIsConnecting(false);
  }, []);

  const handleError = useCallback((err) => {
    console.error('LiveKit connection error:', err);
    setIsConnecting(false);
    setConnectionDetails(null);
  }, []);

  if (!connectionDetails) {
    return <ConnectionForm onConnect={handleConnect} isConnecting={isConnecting} />;
  }

  return (
    <LiveKitRoom
      serverUrl={connectionDetails.url}
      token={connectionDetails.token}
      connect={true}
      audio={true}
      video={true}
      onConnected={handleConnected}
      onDisconnected={handleDisconnected}
      onError={handleError}
    >
      <RoomView />
    </LiveKitRoom>
  );
}
